import { useCallback, useEffect, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import {
  generateOtpForMFA,
  generateOtpForRegistration,
  generateOtpForVerification,
} from '~/store/otp-verification/thunks/generateOtpThunks.ts';
import { useAppDispatch } from '~/store/appDispatch.ts';
import Timer from '~/components/Timer/Timer.tsx';
import { maskPhoneNumber } from '~/store/utils.ts';
import { otpValues } from '~/constants/common.ts';
import { errorMessages } from '~/constants/errorMessages.ts';
import { useNavigate } from 'react-router';
import { validateOtp } from '~/store/otp-verification/thunks/validOtpThunks.ts';
import ModalAlert from '~/components/modals/ModalAlert/ModalAlert';
import { ERROR_CODES } from '~/constants/errorCodes';
import { Box, Grid, Modal, Paper, Typography } from '@mui/material';
import { styles } from '~/components/OTPCodeModal/styles.ts';
import { OtpInputField } from '~/components/OTPCodeModal/OtpInputField.tsx';
import { clearUsersState } from '~/store/users/slices';
import { FormProvider, useForm } from 'react-hook-form';
import {
  createEmptyOtp,
  getOtpErrorMessage,
  normalizeOtpError,
} from '~/components/OTPCodeModal/utils.ts';

interface IOTPCodeModalProps {
  modalOTPVerification: boolean;
  error: string;
  phone: string;
  token?: string;
  registrationOnPortal?: boolean;
  setError: (error: string) => void;
  onClose?: () => void;
  onValidationSuccess?: (verificationToken?: string) => void | Promise<void>;
  MFAFlow?: boolean;
}

type TOtpFormValues = {
  otp: string[];
};

const OTPCodeModal = ({
  modalOTPVerification,
  error,
  phone,
  token,
  setError,
  onClose,
  onValidationSuccess,
  registrationOnPortal = false,
  MFAFlow = false,
}: IOTPCodeModalProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [showVerification, setShowVerification] = useState(false);
  const [canResend, setCanResend] = useState(true);
  const timerRef = useRef<{ resetTimer: () => void; startTimer: () => void }>(null);
  const [errorModalText, setErrorModalText] = useState<string>('');
  const form = useForm<TOtpFormValues>({
    defaultValues: {
      otp: createEmptyOtp(),
    },
    mode: 'onTouched',
    reValidateMode: 'onChange',
    shouldUnregister: false,
  });
  const { isSubmitting } = form.formState;

  const resetModalState = useCallback(() => {
    form.reset({ otp: createEmptyOtp() });
    setError('');
    setErrorModalText('');
    setShowVerification(false);
    setCanResend(true);
    timerRef.current?.resetTimer();
  }, [form, setError]);

  useEffect(() => {
    if (!modalOTPVerification) {
      resetModalState();
    }
  }, [modalOTPVerification, resetModalState]);

  useEffect(() => {
    if (showVerification && phone !== null) {
      timerRef.current?.startTimer();
    }
  }, [showVerification, phone]);

  const closeModalAlert = () => {
    setErrorModalText('');
    dispatch(clearUsersState());
    navigate('/services');
  };

  const verificationMessage = showVerification
    ? `Код було надіслано на номер телефону ${maskPhoneNumber(phone)}`
    : 'Натисніть «Надіслати код», щоб отримати код підтвердження';

  const sendOtpRequest = async (skipCanResendCheck = false) => {
    if (!skipCanResendCheck && !canResend) return;
    if (!phone) {
      setError('Номер телефону не знайдено');
      return;
    }

    setError('');
    form.reset({ otp: createEmptyOtp() });
    setCanResend(false);
    timerRef.current?.startTimer();

    if (registrationOnPortal) {
      try {
        await dispatch(generateOtpForRegistration(phone));
      } catch {
        setErrorModalText(errorMessages.SERVICE_UNAVAILABLE);
      }
    } else {
      try {
        if (MFAFlow) {
          await dispatch(generateOtpForMFA(phone)).unwrap();
        } else {
          await dispatch(generateOtpForVerification({ phone, token: token! })).unwrap();
        }
      } catch (requestError) {
        const { code, message } = normalizeOtpError(requestError!);
        if (code === ERROR_CODES.BLOCKING_OTP) {
          setErrorModalText(message || errorMessages.SERVICE_UNAVAILABLE);
        } else {
          setErrorModalText(errorMessages.SERVICE_UNAVAILABLE);
        }
      }
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    onClose?.();
  };

  const handleSendCode = () => {
    setShowVerification(true);
    void sendOtpRequest(true);
  };

  const resendOtp = () => {
    void sendOtpRequest();
  };

  const handleInternalSubmit = async (otp: string[]) => {
    setError('');
    const otpValue = otp.join('');

    if (otpValue.length !== otpValues.otp) {
      setError(errorMessages.FIELD_OTP);
      return;
    }

    const data = {
      phone: phone,
      otp: otpValue,
    };

    try {
      const response = await dispatch(validateOtp(data)).unwrap();

      if (response?.code === 0) {
        await onValidationSuccess?.(response.verificationToken);
      } else {
        setError(getOtpErrorMessage(response?.properties?.attemptsLeft));
      }
    } catch (requestError) {
      const { code, message, properties } = normalizeOtpError(requestError!);

      if (code === ERROR_CODES.INVALID_OTP) {
        setError(getOtpErrorMessage(properties?.attemptsLeft));
      } else if (
        code === ERROR_CODES.BLOCKING_OTP ||
        code === ERROR_CODES.DATA_NOT_FOUND
      ) {
        setErrorModalText(message || errorMessages.SERVICE_UNAVAILABLE);
      } else {
        setErrorModalText(errorMessages.SERVICE_UNAVAILABLE);
      }
    }
  };

  const onSubmit = async ({ otp }: TOtpFormValues) => {
    await handleInternalSubmit(otp);
  };

  if (!modalOTPVerification) return null;

  return (
    <FormProvider {...form}>
      <Modal
        open={modalOTPVerification}
        onClose={handleClose}
        sx={styles.modalRoot}
        slotProps={{ backdrop: { sx: styles.backdrop } }}
      >
        <Box sx={styles.modalWrapper}>
          <Paper sx={styles.modalPaper}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <Box sx={styles.modalBody}>
                <Typography sx={styles.header} variant={'h2'}>
                  Автентифікація
                </Typography>
                <Typography variant={'body1'} sx={styles.verificationMessage}>
                  {verificationMessage}
                </Typography>
                {showVerification ? (
                  <>
                    <OtpInputField error={error} />
                    <Grid sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Grid sx={{ display: 'flex', gap: '8px' }}>
                        <Timer
                          ref={timerRef}
                          initialTime={60}
                          onTimeUp={() => setCanResend(true)}
                          style={{ textAlign: 'start' }}
                          autoStart={false}
                        />
                        <Typography variant={'body2'} sx={styles.textError}>
                          {error}
                        </Typography>
                      </Grid>
                      <Typography
                        variant="body2"
                        sx={(theme) => ({
                          ...styles.newCode(theme),
                          ...(!canResend ? styles.disabled : {}),
                        })}
                        onClick={() => (!canResend ? null : resendOtp())}
                      >
                        Надіслати знову?
                      </Typography>
                    </Grid>
                    <Button
                      variant="contained"
                      type="submit"
                      disabled={isSubmitting}
                      sx={styles.button}
                    >
                      Відправити
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleSendCode}
                    type="button"
                    sx={styles.button}
                  >
                    Надіслати код
                  </Button>
                )}
              </Box>
            </form>
          </Paper>
        </Box>
      </Modal>

      <ModalAlert isOpen={!!errorModalText} text={errorModalText} onClose={closeModalAlert} />
    </FormProvider>
  );
};

export default OTPCodeModal;
